import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EncrelioLogo } from "@/components/Logo";
import { useSession } from "@/features/auth/useSession";
import { cn } from "@/lib/utils";
import { normalizePath } from "@/config/seo";
import { BRAND } from "./brand";
import { CtaLink } from "./CtaLink";
import { useScrollSpy } from "./motion/useScrollSpy";

const NAV = [
  { to: "/#fonctionnement", label: "Fonctionnement", spy: "fonctionnement" },
  { to: "/#connexions", label: "Connexions", spy: "connexions" },
  { to: "/#cabinets", label: "Cabinets", spy: "cabinets" },
  { to: "/tarifs", label: "Tarifs" },
] as const;
const SPY_IDS = NAV.flatMap((l) => ("spy" in l ? [l.spy] : []));

/** La ligne d'encre qui avance avec la lecture de la page. */
function ReadingLine() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const smooth = useSpring(scrollYProgress, { stiffness: 260, damping: 40, mass: 0.4 });
  return (
    <motion.div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-action"
      style={{ scaleX: reduce ? scrollYProgress : smooth }}
    />
  );
}

export function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const { session } = useSession();
  const { pathname } = useLocation();
  const spied = useScrollSpy(SPY_IDS, pathname === "/");
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const isActive = (link: (typeof NAV)[number]) =>
    "spy" in link ? pathname === "/" && spied === link.spy : normalizePath(pathname) === link.to;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b transition-[background-color,border-color,backdrop-filter] duration-200",
        scrolled || open
          ? "border-border bg-canvas/88 backdrop-blur-md"
          : "border-transparent bg-canvas/0",
      )}
    >
      <div className="mx-auto flex h-[4.5rem] max-w-[1200px] items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          aria-label={`${BRAND}, accueil`}
          className="group -m-2 flex cursor-pointer items-center rounded-lg p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <EncrelioLogo height={28} />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV.map((link) => {
              const active = isActive(link);
              return (
                <li key={link.to} className="relative">
                  <Link
                    to={link.to}
                    aria-current={active ? (("spy" in link) ? "location" : "page") : undefined}
                    className={cn(
                      "inline-flex h-11 cursor-pointer items-center rounded-md px-3 text-sm font-semibold transition-colors duration-150",
                      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      aria-hidden="true"
                      className="absolute inset-x-3 bottom-1.5 h-0.5 rounded-full bg-action"
                      transition={{ duration: reduce ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {session ? (
            <CtaLink to="/app" size="md" arrow>
              Ouvrir l’application
            </CtaLink>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex h-11 cursor-pointer items-center rounded-lg px-3 text-sm font-semibold text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                Se connecter
              </Link>
              <CtaLink to="/signup" size="md">
                Créer un compte
              </CtaLink>
            </>
          )}
        </div>

        <button
          ref={toggleRef}
          type="button"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
          aria-controls="menu-mobile"
          onClick={() => setOpen((v) => !v)}
          className="grid size-11 cursor-pointer place-items-center rounded-lg text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.span
              key={open ? "close" : "open"}
              initial={reduce ? false : { rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={reduce ? undefined : { rotate: 90, opacity: 0 }}
              transition={{ duration: 0.16 }}
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      <ReadingLine />

      <AnimatePresence>
        {open && (
          <motion.nav
            id="menu-mobile"
            aria-label="Menu mobile"
            initial={reduce ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-border bg-canvas lg:hidden"
          >
            <ul className="mx-auto flex max-w-[1200px] flex-col px-4 py-3">
              {NAV.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={reduce ? false : { opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: reduce ? 0 : 0.04 + i * 0.04, duration: 0.22 }}
                >
                  <Link
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="flex h-12 cursor-pointer items-center rounded-lg px-3 font-display text-lg font-semibold text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="mx-auto flex max-w-[1200px] flex-col gap-2 border-t border-border px-4 pt-4 pb-5">
              {session ? (
                <CtaLink to="/app" onClick={() => setOpen(false)} className="w-full">
                  Ouvrir l’application
                </CtaLink>
              ) : (
                <>
                  <CtaLink to="/signup" onClick={() => setOpen(false)} className="w-full">
                    Créer un compte
                  </CtaLink>
                  <CtaLink
                    to="/login"
                    variant="secondary"
                    onClick={() => setOpen(false)}
                    className="w-full"
                  >
                    Se connecter
                  </CtaLink>
                </>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
